import { useId, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { setPrepRule } from '@autoday/core';
import { getStore } from '../../store/appStore.ts';
import { Button } from '../../ui/kit.tsx';
import { errorText, toast } from '../../ui/toast.tsx';
import { randomId } from '../../platform.ts';
import { useLang } from '../../i18n/i18n.ts';

/**
 * Erbjudande efter FÖRSTA godkända planen för en importkälla utan regel. Inget förvalt samtycke: regeln
 * skapas bara med "Föreslå automatiskt", och "Inte nu" ändrar ingenting. Förslagen räknas när appen är
 * öppen och läggs aldrig in utan godkännande.
 */
export function RuleOffer({ sourceId, sourceLabel, templateId, templateName, onDone }: { sourceId: string; sourceLabel: string; templateId: string; templateName: string; onDone: () => void }) {
  const { t } = useLang();
  const ids = useId();
  const [busy, setBusy] = useState(false);
  const [opId] = useState(() => randomId('rule_'));

  async function enable() {
    if (busy) return;
    setBusy(true);
    const store = getStore();
    try {
      await store.run((s) => setPrepRule(s, opId, { sourceId, templateId, status: 'active' }, store.clock.now()));
      toast({ kind: 'ok', text: t(`Automatiska förslag är på för ${sourceLabel}.`, `Automatic suggestions are on for ${sourceLabel}.`) });
      onDone();
    } catch (e) {
      toast({ kind: 'error', text: errorText(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-accent-line bg-accent-soft/40 p-3 text-sm" role="group" aria-labelledby={`${ids}-q`}>
      <p id={`${ids}-q`} className="flex items-start gap-2 font-medium">
        <Sparkles size={16} aria-hidden className="mt-0.5 shrink-0 text-accent" />
        <span className="[overflow-wrap:anywhere]">{t(`Vill du få samma förslag inför kommande deadlines från ${sourceLabel}?`, `Would you like the same suggestion before upcoming deadlines from ${sourceLabel}?`)}</span>
      </p>
      <p className="mt-1 text-muted [overflow-wrap:anywhere]">
        {t(
          `AutoDay föreslår då en plan med mallen "${templateName}" inför varje ny deadline. Förslagen räknas fram när appen är öppen, och inget läggs in utan att du godkänner det.`,
          `AutoDay then suggests a plan with the template "${templateName}" before each new deadline. Suggestions are worked out while the app is open, and nothing is added without your approval.`,
        )}
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button variant="primary" disabled={busy} onClick={() => void enable()}>
          {t('Föreslå automatiskt', 'Suggest automatically')}
        </Button>
        <Button variant="ghost" onClick={onDone}>
          {t('Inte nu', 'Not now')}
        </Button>
      </div>
    </div>
  );
}
